import React, { useCallback, useEffect, useMemo } from 'react';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { WriteBackToAshbyPlaceToWrite } from 'libs/end-to-end/src/types/databases/workflows/strategies/ashby-write-back';
import { WriteBackToAshbyStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/ashby-write-back';
import { ConfigureWriteBackAshbyPlacesToWrite } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackAshbyPlacesToWrite';
import { useConnectionsConfiguration } from 'apps/sigma2-ui/src/lib/hooks/useConnectionsConfiguration';

function AshbyConnectionSelector({
  organizationId,
}: {
  organizationId: string;
}) {
  const connections = useConnectionsConfiguration(organizationId);
  const isAvailable = useMemo(() => {
    if (connections.state !== 'succeeded' || !connections.config?.isEnabled) {
      return false;
    }
    const ashby = connections.config.connections?.ashby;
    return (
      ashby?.isEnabled === true && ashby?.installation?.isInstalled === true
    );
  }, [connections]);

  return (
    <div className="space-y-4">
      {connections.state === 'loading' && (
        <p className="text-gray-600">Loading connections...</p>
      )}
      {connections.state === 'succeeded' && isAvailable && (
        <p className="text-sm text-gray-600">
          Using the organization&apos;s Ashby connection.
        </p>
      )}
      {connections.state === 'succeeded' && !isAvailable && (
        <p className="text-gray-600">
          No Ashby connection is configured for this organization. Configure it
          in Organization Settings → Connections.
        </p>
      )}
      {connections.state === 'failed' && (
        <p className="text-red-600">
          {connections.errorMessage ?? 'Failed to load connections.'}
        </p>
      )}
    </div>
  );
}

export interface ConfigureWriteBackAshbyStrategyProps
  extends ConfigureWorkflowBaseProps {
  strategy: WriteBackToAshbyStrategy;

  updateStrategy: (strategy: WriteBackToAshbyStrategy) => void;
}

export const ConfigureWriteBackAshbyStrategy = ({
  strategy,
  updateStrategy,
  organizationId,
  listSecretsPattern,
  listDataSetsPattern,
}: ConfigureWriteBackAshbyStrategyProps) => {
  // Ashby only supports the `connection` auth method today, so there is no
  // auth-method picker — set it implicitly instead of asking the admin to
  // choose between one option.
  useEffect(() => {
    if (!strategy.authMethod) {
      updateStrategy({ ...strategy, authMethod: { type: 'connection' } });
    }
  }, [strategy, updateStrategy]);

  const updatePlacesToWrite = useCallback(
    (placesToWrite: WriteBackToAshbyPlaceToWrite[]) => {
      updateStrategy({
        ...strategy,
        placesToWrite,
      });
    },
    [strategy, updateStrategy],
  );

  const selectedPlacesToWrite = useMemo(
    () => strategy.placesToWrite ?? [],
    [strategy.placesToWrite],
  );

  return (
    <div className="space-y-4 p-4 bg-white rounded shadow">
      <h2 className="text-xl font-semibold">Configure Ashby Strategy</h2>
      <p className="text-gray-600">
        Configure the strategy for writing back the hiring readiness score to
        Ashby.
      </p>

      <AshbyConnectionSelector organizationId={organizationId} />

      <ConfigureWriteBackAshbyPlacesToWrite
        placesToWrite={selectedPlacesToWrite}
        updatePlacesToWrite={updatePlacesToWrite}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
        listDataSetsPattern={listDataSetsPattern}
      />
    </div>
  );
};
